package org.multiplatform.kotfolio.components

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.util.Constants.FONT_FAMILY
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val AMBER_500 = Color.rgb(245, 158, 11)
private val DARK_CANVAS = Color.rgb(15, 12, 10)
private val CARD_BG = Color.rgb(24, 20, 18)

val NodePulseKeyframes = Keyframes {
    0.percent {
        Modifier.boxShadow(offsetX = 0.px, offsetY = 0.px, blurRadius = 10.px, color = Color.rgba(245, 158, 11, 0.2f))
    }
    50.percent {
        Modifier.boxShadow(offsetX = 0.px, offsetY = 0.px, blurRadius = 30.px, color = Color.rgba(245, 158, 11, 0.6f))
    }
    100.percent {
        Modifier.boxShadow(offsetX = 0.px, offsetY = 0.px, blurRadius = 10.px, color = Color.rgba(245, 158, 11, 0.2f))
    }
}

val CursorBlinkKeyframes = Keyframes {
    0.percent { Modifier.opacity(1) }
    50.percent { Modifier.opacity(0) }
    100.percent { Modifier.opacity(1) }
}

val TrainTravelKeyframes = Keyframes {
    0.percent {
        Modifier.left((-150).px).opacity(0)
    }
    10.percent {
        Modifier.opacity(1)
    }
    90.percent {
        Modifier.opacity(1)
    }
    100.percent {
        Modifier.left(100.percent).opacity(0)
    }
}

val SkillGlowKeyframes = Keyframes {
    0.percent {
        Modifier.styleModifier { property("filter", "brightness(1.0)") }
    }
    50.percent {
        Modifier.styleModifier { property("filter", "brightness(1.5)") }
    }
    100.percent {
        Modifier.styleModifier { property("filter", "brightness(1.0)") }
    }
}

// Each pill slides in from the left like a train car arriving at a station
val PillSlideInKeyframes = Keyframes {
    from {
        Modifier
            .translateX((-60).px)
            .opacity(0)
    }
    to {
        Modifier
            .translateX(0.px)
            .opacity(1)
    }
}

@Composable
fun MatrixRootNode() {
    Box(
        modifier = Modifier
            .size(100.px)
            .backgroundColor(CARD_BG)
            .border(2.px, LineStyle.Solid, AMBER_500)
            .borderRadius(16.px)
            .animation(
                NodePulseKeyframes.toAnimation(
                    duration = 3.s,
                    iterationCount = AnimationIterationCount.Infinite,
                    timingFunction = AnimationTimingFunction.EaseInOut,
                ),
            ),
        contentAlignment = Alignment.Center,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Span(
                attrs = Modifier
                    .color(AMBER_500)
                    .fontSize(32.px)
                    .fontWeight(FontWeight.Bold)
                    .fontFamily("Courier New", "monospace")
                    .toAttrs(),
            ) { Text(">") }

            // Blinking cursor
            Box(
                modifier = Modifier
                    .width(15.px)
                    .height(30.px)
                    .backgroundColor(AMBER_500)
                    .margin(left = 4.px)
                    .animation(
                        CursorBlinkKeyframes.toAnimation(
                            duration = 1.s,
                            iterationCount = AnimationIterationCount.Infinite,
                        ),
                    ),
            )
        }
    }
}

@Composable
fun MatrixCategoryBranch(
    title: String,
    skills: List<String>,
    accentColor: Color = AMBER_500,
    animate: Boolean = false,
    delayMs: Int = 0,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Spine to Category Connector
        Box(
            modifier = Modifier
                .width(40.px)
                .height(3.px)
                .backgroundColor(accentColor)
                .boxShadow(offsetX = 0.px, offsetY = 0.px, blurRadius = 12.px, color = accentColor),
        )

        // Category Badge
        Box(
            modifier = Modifier
                .padding(leftRight = 12.px, topBottom = 12.px)
                .backgroundColor(DARK_CANVAS)
                .border(2.px, LineStyle.Solid, accentColor)
                .borderRadius(12.px)
                .position(Position.Relative)
                .styleModifier {
                    property("overflow", "hidden")
                },
            contentAlignment = Alignment.Center,
        ) {
            Span(
                attrs = Modifier
                    .color(accentColor)
                    .fontSize(15.px)
                    .fontWeight(FontWeight.Black)
                    .fontFamily(FONT_FAMILY)
                    .styleModifier { property("letter-spacing", "1px") }
                    .toAttrs(),
            ) { Text(title.uppercase()) }
        }

        // Horizontal Track Line to Pills
        Box(
            modifier = Modifier
                .width(40.px)
                .height(3.px)
                .backgroundColor(accentColor)
                .boxShadow(offsetX = 0.px, offsetY = 0.px, blurRadius = 12.px, color = accentColor)
                .position(Position.Relative)
                .styleModifier {
                    property("overflow", "hidden")
                },
        ) {
            if (animate) {
                Box(
                    modifier = Modifier
                        .fillMaxHeight()
                        .width(30.px)
                        .backgroundColor(Colors.White)
                        .position(Position.Absolute)
                        .boxShadow(offsetX = 0.px, offsetY = 0.px, blurRadius = 15.px, color = Colors.White)
                        .animation(
                            TrainTravelKeyframes.toAnimation(
                                duration = 3.s,
                                iterationCount = AnimationIterationCount.Infinite,
                                timingFunction = AnimationTimingFunction.Linear,
                                delay = (200 + delayMs).ms,
                            ),
                        ),
                )
            }
        }

        // Main Track with Pill Nodes
        Box(
            modifier = Modifier
                .height(60.px)
                .position(Position.Relative)
                .styleModifier {
                    property("overflow", "visible")
                    property("display", "inline-flex") // wrap to content width = no excess line
                    property("align-items", "center")
                },
        ) {
            // Horizontal Track Line (ABSOLUTELY POSITIONED — only as wide as pills)
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(3.px)
                    .backgroundColor(accentColor)
                    .position(Position.Absolute)
                    .styleModifier {
                        property("top", "50%")
                        property("transform", "translateY(-50%)")
                        property("opacity", "0.6")
                        property("box-shadow", "0 0 10px rgba(255, 255, 255, 0.2)")
                        property("z-index", "0")
                    },
            )

            // THE TRAIN: Moving pulse
            if (animate) {
                Box(
                    modifier = Modifier
                        .height(40.px)
                        .width(150.px)
                        .position(Position.Absolute)
                        .styleModifier {
                            property("top", "50%")
                            property("transform", "translateY(-50%)")
                            property("background", "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)")
                            property("box-shadow", "0 0 25px rgba(255, 255, 255, 0.2)")
                            property("z-index", "1")
                        }
                        .animation(
                            TrainTravelKeyframes.toAnimation(
                                duration = 4.s,
                                iterationCount = AnimationIterationCount.Infinite,
                                timingFunction = AnimationTimingFunction.Linear,
                                delay = (500 + delayMs).ms,
                            ),
                        ),
                )
            }

            // Pills sit ON TOP of the line via z-index, arrive staggered like train cars
            Row(
                modifier = Modifier
                    .height(60.px)
                    .styleModifier { property("z-index", "2") },
                horizontalArrangement = Arrangement.spacedBy(16.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                skills.forEachIndexed { index, skill ->
                    MatrixSkillPill(skill, accentColor, animate, delayMs, index)
                }
            }
        }
    }
}

@Composable
fun MatrixSkillPill(
    name: String,
    accentColor: Color,
    animate: Boolean = false,
    branchDelayMs: Int = 0,
    pillIndex: Int = 0,
) {
    // Each pill arrives 150ms after the previous — like train cars
    val arrivalDelayMs = branchDelayMs + (pillIndex * 150)
    val glowDelayMs = arrivalDelayMs + 500 // Glow kicks in after slide-in completes

    Box(
        modifier = Modifier
            .padding(leftRight = 16.px, topBottom = 8.px)
            .backgroundColor(Color.rgba(15, 12, 10, 1.0f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.15f))
            .borderRadius(20.px)
            .styleModifier {
                property("backdrop-filter", "blur(10px)")
                property("white-space", "nowrap")
                property("display", "flex")
                property("align-items", "center")
                property("z-index", "2")
            }
            .then(
                if (animate) {
                    Modifier.animation(
                        PillSlideInKeyframes.toAnimation(
                            duration = 0.5.s,
                            timingFunction = AnimationTimingFunction.cubicBezier(0.25, 0.46, 0.45, 0.94),
                            delay = arrivalDelayMs.ms,
                            fillMode = AnimationFillMode.Both,
                        ),
                        SkillGlowKeyframes.toAnimation(
                            duration = 4.s,
                            timingFunction = AnimationTimingFunction.EaseInOut,
                            delay = glowDelayMs.ms,
                            iterationCount = AnimationIterationCount.Infinite,
                        ),
                    )
                } else {
                    Modifier
                },
            ),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .size(8.px)
                    .borderRadius(50.percent)
                    .backgroundColor(accentColor)
                    .margin(right = 10.px)
                    .boxShadow(offsetX = 0.px, offsetY = 0.px, blurRadius = 8.px, color = accentColor),
            )

            Span(
                attrs = Modifier
                    .color(Color.rgb(220, 220, 220))
                    .fontSize(14.px)
                    .fontWeight(FontWeight.Medium)
                    .fontFamily(FONT_FAMILY)
                    .toAttrs(),
            ) { Text(name) }
        }
    }
}
